import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import type { OutletDirectoryRow, TenantProfile } from '@kilat/shared';
import { geo as geoUtil } from '@kilat/shared';
import {
  GeoMap,
  backLinkStyle,
  eyebrowStyle,
  pageStyle,
  type GeoMapLegendItem,
  type GeoMarker,
  type GeoMarkerTone,
} from '@kilat/ui';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';
import { listDirectoryForBrand } from '../lib/repositories/outlet-directory-repo.js';
import { logger } from '../lib/logger.js';

/**
 * /superadmin/map — a network map of every listed outlet in the CURRENT
 * platform brand, coloured by the owning tenant's subscription status
 * (feature: map-dashboards).
 *
 * Read-scoping (no rules change, no widening):
 *   - `tenantsRepo.list()` — tenants are public-read; client-filtered to the
 *     current brand (`platformBrandId`), exactly like /superadmin/tenants.
 *   - `listDirectoryForBrand(brand.id)` — the directory is the cross-tenant,
 *     geo-bearing projection. A super-admin may read their OWN brand's rows,
 *     listed or not (firestore.rules); the query must carry the brand filter
 *     or Firestore denies the whole list (rules are not filters).
 * Both are one-shot reads (a map is a snapshot, not a live board) and the page
 * is super-admin-gated inline, identical to the other /superadmin surfaces.
 * No money / auth / write logic is touched.
 */

type TenantStatus = TenantProfile['status'];

interface TenantRootShape {
  readonly id?: string;
  readonly profile: TenantProfile;
}

type LoadState =
  | { readonly phase: 'loading' }
  | {
      readonly phase: 'ready';
      readonly rows: readonly OutletDirectoryRow[];
      readonly tenantById: ReadonlyMap<
        string,
        { readonly name: string; readonly status: TenantStatus }
      >;
    }
  | { readonly phase: 'error' };

const LEGEND_ORDER: readonly TenantStatus[] = [
  'active',
  'trial',
  'past_due',
  'suspended',
  'archived',
];

function statusTone(status: TenantStatus): GeoMarkerTone {
  switch (status) {
    case 'active':
      return 'positive';
    case 'trial':
      return 'primary';
    case 'past_due':
      return 'warning';
    case 'suspended':
      return 'danger';
    case 'archived':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function legendKey(status: TenantStatus): string {
  return status === 'past_due' ? 'pastDue' : status;
}

export function SuperadminMapPage() {
  const { claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const { t } = useI18n();
  const isSuper = useMemo(() => isSuperAdminForBrand(claims, brand.id), [claims, brand.id]);

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!isSuper) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    Promise.all([tenantsRepo.list(), listDirectoryForBrand(brand.id)])
      .then(([tenants, rows]) => {
        if (cancelled) return;
        const tenantById = new Map<string, { name: string; status: TenantStatus }>();
        for (const tRaw of tenants) {
          const tt = tRaw as TenantRootShape;
          if (!tt.id) continue;
          if (tt.profile.platformBrandId !== brand.id) continue;
          tenantById.set(tt.id, { name: tt.profile.businessName, status: tt.profile.status });
        }
        // The query is already brand-scoped; the tenant join stays as a second
        // guard and supplies each pin's business name + status.
        const scoped = rows.filter((r) => tenantById.has(r.tenantId));
        setState({ phase: 'ready', rows: scoped, tenantById });
      })
      .catch((e: unknown) => {
        logger.error('superadmin map: load failed', { error: String(e) });
        if (!cancelled) setState({ phase: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [isSuper, brand.id, attempt]);

  const view = useMemo(() => {
    if (state.phase !== 'ready') {
      return { markers: [] as GeoMarker[], mappedTenants: 0, unmappedNames: [] as string[] };
    }
    const parts = geoUtil.partitionByLocation(state.rows, (r) => r.geo);
    const tenantsWithPin = new Set<string>();
    const markers: GeoMarker[] = parts.placed.map((r) => {
      tenantsWithPin.add(r.tenantId);
      const tenant = state.tenantById.get(r.tenantId);
      const status: TenantStatus = tenant?.status ?? 'active';
      return {
        id: r.id,
        lat: r.geo.lat,
        lng: r.geo.lng,
        label: r.name,
        tone: statusTone(status),
        popup: (
          <NetworkPopup
            name={r.name}
            operator={tenant?.name ?? r.name}
            statusLabel={t(`superadmin.map.legend.${legendKey(status)}`)}
            address={r.address}
          />
        ),
      };
    });
    // Operators (brand tenants) with no placeable outlet on the map.
    const unmappedNames: string[] = [];
    for (const [tid, tenant] of state.tenantById) {
      if (!tenantsWithPin.has(tid)) unmappedNames.push(tenant.name);
    }
    return { markers, mappedTenants: tenantsWithPin.size, unmappedNames };
  }, [state, t]);

  const legend: GeoMapLegendItem[] = LEGEND_ORDER.map((s) => ({
    tone: statusTone(s),
    label: t(`superadmin.map.legend.${legendKey(s)}`),
  }));

  if (authLoading) {
    return (
      <main style={pageStyle}>
        <p role="status" style={mutedStyle}>
          {t('superadmin.map.loading')}
        </p>
      </main>
    );
  }

  if (!isSuper) {
    return (
      <main style={pageStyle}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h1)' }}>
          {t('superadmin.map.notAdminTitle')}
        </h1>
        <p style={mutedStyle}>{t('superadmin.map.notAdminBody', { brand: brand.name })}</p>
        <Link to="/queue" style={linkStyle}>
          {t('superadmin.map.back')}
        </Link>
      </main>
    );
  }

  return (
    <main style={pageStyle}>
      <header style={{ marginBottom: 'var(--space-6)' }}>
        <p style={eyebrowStyle}>{t('dashboard.nav.superadmin')}</p>
        <h1 style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--text-h1)' }}>
          {t('superadmin.map.title')}
        </h1>
        <p style={{ ...mutedStyle, maxWidth: 640 }}>
          {t('superadmin.map.subtitle', { brand: brand.name })}
        </p>
        <Link to="/queue" style={linkStyle}>
          {t('superadmin.map.back')}
        </Link>
      </header>

      {state.phase === 'loading' ? (
        <p role="status" style={mutedStyle}>
          {t('superadmin.map.loading')}
        </p>
      ) : state.phase === 'error' ? (
        <div role="alert" style={{ maxWidth: 480 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--text-h2)' }}>
            {t('superadmin.map.errorTitle')}
          </h2>
          <p style={mutedStyle}>{t('superadmin.map.errorBody')}</p>
          <button type="button" onClick={() => setAttempt((a) => a + 1)} style={retryBtnStyle}>
            {t('superadmin.map.retry')}
          </button>
        </div>
      ) : view.markers.length === 0 ? (
        <div style={{ maxWidth: 520 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--text-h2)' }}>
            {t('superadmin.map.empty', { brand: brand.name })}
          </h2>
          {view.unmappedNames.length > 0 ? (
            <p style={mutedStyle}>
              {t('superadmin.map.unmappedTitle', { count: view.unmappedNames.length })}
            </p>
          ) : null}
        </div>
      ) : (
        <>
          <p style={{ ...mutedStyle, marginTop: 0 }}>
            {t('superadmin.map.summary', {
              outlets: view.markers.length,
              operators: view.mappedTenants,
            })}
          </p>
          <GeoMap
            markers={view.markers}
            ariaLabel={t('superadmin.map.mapAria')}
            height={520}
            legend={legend}
          />
          {view.unmappedNames.length > 0 ? (
            <section style={unmappedCardStyle}>
              <h2 style={{ margin: '0 0 var(--space-1)', fontSize: 'var(--text-h3)' }}>
                {t('superadmin.map.unmappedTitle', { count: view.unmappedNames.length })}
              </h2>
              <p style={{ margin: '0 0 var(--space-3)', color: 'var(--color-on-surface-muted)' }}>
                {t('superadmin.map.unmappedBody')}
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
                {view.unmappedNames.map((name) => (
                  <li key={name} style={{ fontSize: 'var(--text-meta)' }}>
                    {name}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
    </main>
  );
}

function NetworkPopup({
  name,
  operator,
  statusLabel,
  address,
}: {
  readonly name: string;
  readonly operator: string;
  readonly statusLabel: string;
  readonly address: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 160 }}>
      <strong style={{ fontSize: 'var(--text-body)' }}>{name}</strong>
      {operator !== name ? (
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {operator}
        </span>
      ) : null}
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {statusLabel}
      </span>
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {address}
      </span>
    </div>
  );
}

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

const mutedStyle: CSSProperties = {
  margin: 'var(--space-2) 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const linkStyle: CSSProperties = {
  // `inline-block` makes `min-height` inert; `backLinkStyle` is inline-flex.
  ...backLinkStyle,
  marginTop: 'var(--space-3)',
  color: 'var(--color-primary)',
  fontWeight: 600,
};

const unmappedCardStyle: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-1)',
  padding: 'var(--space-4) var(--space-5)',
};

const retryBtnStyle: CSSProperties = {
  marginTop: 'var(--space-4)',
  minHeight: 'var(--touch-target)',
  padding: '0 var(--space-5)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontWeight: 600,
  fontSize: 'var(--text-body)',
  cursor: 'pointer',
};
